/* eslint-disable react/require-default-props */
import React, { useContext } from 'react';
import proptypes from 'prop-types';
import {
  Modal,
  ModalHeader,
  ModalBody,
  Table,
  TableBody,
  TableCell,
  TableRow,
  LoadingButton,
  Typography,
  ModalFooter,
  TableContainer
} from '@one-thd/sui-atomic-components';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { RECOMMENDED_CLEARANCE } from '../constants';

const {
  string, bool, number, func, shape, oneOfType
} = proptypes;

export const FitBreakdownModal = ({
  open,
  onClose,
  fitResult = null,
  userDimensions,
  setDrawerOpen,
  secondaryHeader = '',
  resultMessage = '',
  includeClearance = true,
  overhangTolerance
}) => {
  const { channel } = useContext(ExperienceContext);
  const isMobile = channel === 'mobile';

  if (!fitResult) return null;

  const getDimensionStatus = (userDim, productDim, isDepth) => {
    const user = parseFloat(userDim) || 0;
    const product = parseFloat(productDim) || 0;
    const difference = user - product;
    
    let fitStatus;
    if (difference >= 0 || (Math.abs(difference) <= overhangTolerance && isDepth)) {
      fitStatus = 'fits';
    } else if (Math.abs(difference) >= overhangTolerance && isDepth) {
      fitStatus = 'mostlyFits';
    } else {
      fitStatus = 'doesNotFit';
    }

    return {
      fitStatus,
      difference,
      user,
      product
    };
  };

  const calcRoundedDifference = (difference) => {
    const usableDifference = Math.round(difference * 1000) / 10;
    return parseFloat(Math.max(0, difference).toFixed(3));

  }

  const handleMeasureTipsClick = () => {
    onClose();
    setDrawerOpen(true);
  };

  if (!fitResult) return null;

  const widthStatus = getDimensionStatus(userDimensions.width, fitResult.productDimensions.calculatedWidth);
  const heightStatus = getDimensionStatus(userDimensions.height, fitResult.productDimensions.calculatedHeight);
  const depthStatus = getDimensionStatus(userDimensions.depth, fitResult.productDimensions.calculatedDepth, true);

  return (
    <Modal open={open} onClose={onClose} size="lg">
      <ModalHeader
        onClose={onClose}
      >
        <h2 className="sui-text-xl sui-font-bold">
          Breakdown
        </h2>
      </ModalHeader>

      <ModalBody className={`sui-w-full sui-leading-tight ${isMobile ? 'sui-overflow-x-auto sui-no-scrollbar' : ''}`}>
        <div className="sui-mb-6 sui-leading-tight" data-testid="fit-breakdown-modal-header-message">
          <div className="sui-mb-1"><Typography variant="h5">{secondaryHeader}</Typography></div>
          <Typography className="sui-pt-1">{resultMessage}</Typography>
        </div>
        <TableContainer>
          <Table
            layout="fixed"
            density="loose"
            
          >
            <TableRow>
              <TableCell
                width={120}
                sticky="horizontal"
                className={`sui-border-l-0 sui-px-4 sui-border-b-1 sui-text-left sui-border-primary sui-border-solid sui-z-10 sui-bg-subtle sui-sticky sui-left-0 sui-shadow-[2px_0px_4px_rgba(0,0,0,0.1),6px_0px_4px_rgba(0,0,0,0.1)] 2xl:sui-shadow-none`}
                component={isMobile ? 'th' : 'td'}
                variant="head"
              >
                <Typography weight="bold">Dimension</Typography>
              </TableCell>
              <TableCell
                width={175}
                component="td"
                variant="body"
              >
                <Typography weight="bold">Fit Status</Typography>
              </TableCell>
              <TableCell
                width={175}
                component="td"
                variant="body"
              >
                <Typography weight="bold">Customer&apos;s Space</Typography>
              </TableCell>
              <TableCell
                width={175}
                component="td"
                variant="body"
              >
                <Typography weight="bold">Fridge Size</Typography>
              </TableCell>
              <TableCell
                width={175}
                component="td"
                variant="body"
                className="sui-border-r-0 sui-p-4 sui-border-b-1 sui-border-primary sui-border-solid"
              >
                <Typography weight="bold">Recommended Clearance</Typography>
                {!includeClearance && <Typography variant="body-sm">(not included in calc.)</Typography>}
              </TableCell>
            </TableRow>
            <TableBody>
              {[
                {
                  dimension: 'Height',
                  fitStatus: heightStatus,
                  fitIcon: fitResult.heightIcon,
                  customerSpace: userDimensions.height,
                  fridgeSize: fitResult.productDimensions.height,
                  calculatedFridgeSize: fitResult.productDimensions.calculatedHeight,
                  recommendedClearance: RECOMMENDED_CLEARANCE.height,
                  clearanceQuantifier: '(from top)',
                  sizeQuantifier: ''
                },
                {
                  dimension: 'Width',
                  fitStatus: widthStatus,
                  fitIcon: fitResult.widthIcon,
                  customerSpace: userDimensions.width,
                  fridgeSize: fitResult.productDimensions.width,
                  calculatedFridgeSize: fitResult.productDimensions.calculatedWidth,
                  recommendedClearance: RECOMMENDED_CLEARANCE.width / 2,
                  clearanceQuantifier: '(each side)',
                  sizeQuantifier: ''
                },
                {
                  dimension: 'Depth',
                  fitStatus: depthStatus,
                  fitIcon: fitResult.depthIcon,
                  customerSpace: userDimensions.depth,
                  fridgeSize: fitResult.productDimensions.depth,
                  calculatedFridgeSize: fitResult.productDimensions.calculatedDepth,
                  recommendedClearance: RECOMMENDED_CLEARANCE.depth,
                  clearanceQuantifier: '(from back)',
                  sizeQuantifier: '(excluding handles)'
                }
              ].map(({
                dimension,
                fitStatus,
                fitIcon,
                customerSpace,
                fridgeSize,
                calculatedFridgeSize,
                recommendedClearance,
                clearanceQuantifier,
                sizeQuantifier
              }) => (
                <TableRow key={dimension}>
                  <TableCell
                    width={120}
                    sticky="horizontal"
                    className="sui-border-l-0 sui-px-4 sui-border-t-1 sui-text-left sui-border-primary sui-border-solid sui-z-10 sui-bg-subtle sui-sticky sui-left-0 sui-shadow-[2px_0px_4px_rgba(0,0,0,0.1),6px_0px_4px_rgba(0,0,0,0.1)] 2xl:sui-shadow-none"
                    component={isMobile ? 'th' : 'td'}
                    variant={isMobile ? 'head' : 'body'}
                  >
                    <Typography weight="bold">{dimension}</Typography>
                  </TableCell>
                  <TableCell className="sui-border-l-0 sui-p-4 sui-border-t-1 sui-border-primary sui-border-solid">
                    <div className="sui-flex sui-items-start sui-gap-2">
                      <span className="sui-inline-flex sui-shrink-0">{fitIcon}</span>
                      <span className="sui-font-medium">
                        {(() => {
                          if (fitStatus.fitStatus === 'fits') {
                            const diff = customerSpace - calculatedFridgeSize;
                            const roundedDiff = calcRoundedDifference(Math.abs(diff));
                            if (dimension === 'Depth' && diff < 0) {                                                            
                              return `Fits (sticks out ${roundedDiff} inches past countertop)`;
                            }                            
                            return roundedDiff === 0 ? 'Fits' : `Fits +${roundedDiff} in.`;
                          }
                          if (fitStatus.fitStatus === 'mostlyFits') {
                            return `Mostly Fits (sticks out ${calcRoundedDifference(Math.abs(customerSpace - calculatedFridgeSize))} inches past countertop)`;
                          }
                          return `Doesn't Fit -${calcRoundedDifference(Math.abs(calculatedFridgeSize - customerSpace))} in.`;
                        })()}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell
                    width={280}
                    className="sui-border-l-0 sui-p-4 sui-border-t-1 sui-border-primary sui-border-solid"
                  >
                    {customerSpace || '0'} in.
                  </TableCell>
                  <TableCell
                    width={280}
                    className="sui-border-l-0 sui-p-4 sui-border-t-1 sui-border-primary sui-border-solid"
                  >
                    {fridgeSize} in. {sizeQuantifier}
                  </TableCell>
                  <TableCell
                    width={280}
                    className="sui-border-l-0 sui-p-4 sui-border-t-1 sui-border-primary sui-border-solid"
                  >
                    {recommendedClearance} in. {clearanceQuantifier} 
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <div className="sui-mt-6">
          <Typography height="tight">
            <p>
              <strong>Tip: </strong>
              Don&apos;t forget to make sure the appliance fits through your front door and other narrow spaces.
              For more tips click below.
            </p>
          </Typography>
        </div>
      </ModalBody>
      <ModalFooter position="end">
        <LoadingButton
          onClick={handleMeasureTipsClick}
          variant="tertiary"
          size="small"
          data-testid="fit-compatibility-measure-install-tips"
        >
          Measure & Install Tips
        </LoadingButton>
      </ModalFooter>
    </Modal>
  );
};

FitBreakdownModal.propTypes = {
  open: bool.isRequired,
  onClose: func.isRequired,
  includeClearance: bool,
  fitResult: shape({
    productDimensions: shape({
      width: oneOfType([string, number]),
      height: oneOfType([string, number]),
      depth: oneOfType([string, number])
    })
  }),
  userDimensions: shape({
    width: oneOfType([string, number]),
    height: oneOfType([string, number]),
    depth: oneOfType([string, number])
  }).isRequired,
  setDrawerOpen: func.isRequired,
  secondaryHeader: string,
  resultMessage: string,
};


